import { isNil, map, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { merchantsApi } from 'api';
import { FormikSelect, FormikSelectProps } from 'components';
import { QueryKey } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Merchant } from 'types';

type Props = {
  name: string;
  label?: string;
  merchants?: Merchant[];
  getDisplayName?: (traderId: string) => string | undefined;
} & Partial<FormikSelectProps>;

export const MerchantSelect: React.FC<Props> = ({
  name,
  merchants,
  label,
  getDisplayName,
  ...rest
}: Props) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.merchant_select',
  });
  const { role } = useUser();

  const queryResult = useQuery(
    QueryKey.Merchants,
    () => merchantsApi.getAllAsRole(role)(),
    { enabled: isNil(merchants) },
  );

  const options = useMemo(
    () =>
      map(
        sortBy(
          merchants || queryResult?.data,
          (merchant) => merchant.user?.name,
        ),
        (merchant) => ({
          value: merchant.id,
          label:
            getDisplayName?.(merchant.id) ||
            merchant.user?.name ||
            merchant.user?.email ||
            merchant.id,
        }),
      ),
    [merchants, queryResult?.data, getDisplayName],
  );

  return (
    <FormikSelect
      noneOption
      label={label || t('label')}
      {...rest}
      name={name}
      options={options}
    />
  );
};
